/**
 * i18n provider — wires the shared @obrava/shared i18next instance into
 * react-i18next.
 *
 * The instance is built with the shared `createI18n()` factory (single source
 * of the ca/es catalogs, parity-checked) and the react-i18next binding is
 * registered via `initReactI18next`. Default language is 'ca' (CLAUDE.md).
 * The instance is passed explicitly to `I18nextProvider` so `useTranslation`
 * reads this configured instance rather than any global singleton.
 */

import React from 'react';
import { I18nextProvider, initReactI18next } from 'react-i18next';
import { createI18n, APP_LANGUAGES } from '@obrava/shared';
import type { AppLanguage } from '@obrava/shared';

const STORAGE_KEY = 'obrava.platform-console.language';

function readStoredLanguage(): AppLanguage | null {
  const v = localStorage.getItem(STORAGE_KEY);
  return (APP_LANGUAGES as readonly string[]).includes(v ?? '')
    ? (v as AppLanguage)
    : null;
}

const initialLanguage: AppLanguage = readStoredLanguage() ?? 'ca';

const i18n = createI18n({
  lng: initialLanguage,
  fallbackLng: 'ca',
  assertParity: import.meta.env.DEV,
});
i18n.use(initReactI18next);

const LangContext = React.createContext<{
  readonly language: AppLanguage;
  readonly setLanguage: (lng: AppLanguage) => void;
} | null>(null);

export function AppI18nProvider(props: {
  readonly children: React.ReactNode;
}): React.ReactElement {
  const [language, setLanguageState] = React.useState<AppLanguage>(initialLanguage);

  // Reflect the initial (possibly persisted) language on the document root.
  React.useEffect(() => {
    document.documentElement.lang = initialLanguage;
  }, []);

  const setLanguage = React.useCallback((lng: AppLanguage) => {
    void i18n.changeLanguage(lng);
    setLanguageState(lng);
    document.documentElement.lang = lng;
    localStorage.setItem(STORAGE_KEY, lng);
  }, []);

  const value = React.useMemo(() => ({ language, setLanguage }), [language, setLanguage]);

  return (
    <I18nextProvider i18n={i18n}>
      <LangContext.Provider value={value}>{props.children}</LangContext.Provider>
    </I18nextProvider>
  );
}

export function useLanguage(): {
  readonly language: AppLanguage;
  readonly setLanguage: (lng: AppLanguage) => void;
} {
  const ctx = React.useContext(LangContext);
  if (!ctx) {
    throw new Error('useLanguage must be used within AppI18nProvider');
  }
  return ctx;
}
